import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Tree, Card, Divider, Select, Tag, Spin, List, Checkbox } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import { collectionAPI, productAPI } from '../api';
import MultiLevelJsonEditor from './Utils/MultiLevelJsonEditor';
import ProductSearchSelector from '../utils/ProductSearchSelector';

const CollectionManagement = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [multiLevelData, setMultiLevelData] = useState({});
  const [multiLevelLoading, setMultiLevelLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  
  // 编辑商品集中的商品
  const [productsModalVisible, setProductsModalVisible] = useState(false);
  const [editingCollectionProducts, setEditingCollectionProducts] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [addProductModalVisible, setAddProductModalVisible] = useState(false);
  const [selectedProductsToDelete, setSelectedProductsToDelete] = useState([]);
  const [productsDetails, setProductsDetails] = useState([]);

  useEffect(() => {
    fetchCollections();
    fetchMultiLevelData();
  }, []);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await collectionAPI.getAll();
      setCollections(response.data);
    } catch (error) {
      message.error('获取商品集列表失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };



  // 转换旧格式数据：将纯数字键转换为 "col_" 前缀格式
  const migrateDataFormat = (data) => {
    const newData = {};
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'number') {
        // 叶子节点：如果键是纯数字，转换为 "col_数字" 格式
        const newKey = /^\d+$/.test(key) ? `col_${key}` : key;
        newData[newKey] = value;
      } else if (typeof value === 'object' && value !== null) {
        // 非叶子节点：递归处理
        newData[key] = migrateDataFormat(value);
      } else {
        newData[key] = value;
      }
    });
    return newData;
  };

  const fetchMultiLevelData = async () => {
    try {
      const response = await collectionAPI.getMultiLevel();
      let data = response.data.data || {};
      // 自动迁移旧格式数据
      data = migrateDataFormat(data);
      setMultiLevelData(data);
    } catch (error) {
      message.error('获取多级商品集分类数据失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const saveMultiLevelData = async () => {
    try {
      setMultiLevelLoading(true);
      await collectionAPI.updateMultiLevel(multiLevelData);
      message.success('多级商品集分类数据保存成功');
    } catch (error) {
      message.error('保存多级商品集分类数据失败: ' + (error.response?.data?.detail || error.message));
      throw error; // 重新抛出错误，让MultiLevelJsonEditor知道保存失败
    } finally {
      setMultiLevelLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCollection(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCollection(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await collectionAPI.delete(id);
      message.success('删除成功');
      fetchCollections();
    } catch (error) {
      message.error('删除失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCollection) {
        await collectionAPI.update(editingCollection.id, values);
        message.success('更新成功');
      } else {
        await collectionAPI.create(values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchCollections();
    } catch (error) {
      message.error((editingCollection ? '更新失败' : '创建失败') + ': ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleManageProducts = async (record) => {
    setEditingCollectionProducts(record);
    setLoadingProducts(true);
    setProductsModalVisible(true);
    setSelectedProductsToDelete([]);
    
    try {
      const response = await collectionAPI.getProducts(record.id);
      const productIds = response.data.product_ids || [];
      setSelectedProducts(productIds);
      
      // 获取商品详细信息
      if (productIds.length > 0) {
        const productsData = await Promise.all(
          productIds.map(async (id) => {
            try {
              const productResponse = await productAPI.getById(id);
              return productResponse.data;
            } catch (error) {
              
              return { id, name: '未知商品', error: true };
            }
          })
        );
        setProductsDetails(productsData);
      } else {
        setProductsDetails([]);
      }
    } catch (error) {
      message.error('获取商品集商品失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductsSubmit = async () => {
    if (!editingCollectionProducts) return;
    
    try {
      await collectionAPI.updateProducts(editingCollectionProducts.id, selectedProducts);
      message.success('商品集商品更新成功');
      setProductsModalVisible(false);
      setEditingCollectionProducts(null);
      setSelectedProducts([]);
      setProductsDetails([]);
      setSelectedProductsToDelete([]);
      fetchCollections();
    } catch (error) {
      message.error('更新商品集商品失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddProducts = async (productIds) => {
    // 添加新选择的商品
    const newProductIds = productIds.filter(id => !selectedProducts.includes(id));
    if (newProductIds.length === 0) {
      message.info('所选商品已在商品集中');
      setAddProductModalVisible(false);
      return;
    }

    const updatedProducts = [...selectedProducts, ...newProductIds];
    setSelectedProducts(updatedProducts);

    // 先关闭对话框
    setAddProductModalVisible(false);
    message.success(`成功添加 ${newProductIds.length} 个商品`);

    // 异步获取新添加商品的详细信息
    try {
      const newProductsData = await Promise.all(
        newProductIds.map(async (id) => {
          try {
            const response = await productAPI.getById(id);
            return response.data;
          } catch (error) {
            
            return { id, name: '未知商品', error: true };
          }
        })
      );
      setProductsDetails([...productsDetails, ...newProductsData]);
    } catch (error) {
      
    }
  };

  const handleDeleteSelectedProducts = () => {
    if (selectedProductsToDelete.length === 0) {
      message.warning('请先选择要删除的商品');
      return;
    }

    const updatedProducts = selectedProducts.filter(
      id => !selectedProductsToDelete.includes(id)
    );
    const updatedDetails = productsDetails.filter(
      product => !selectedProductsToDelete.includes(product.id)
    );

    setSelectedProducts(updatedProducts);
    setProductsDetails(updatedDetails);
    setSelectedProductsToDelete([]);
    message.success(`成功删除 ${selectedProductsToDelete.length} 个商品`);
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    });
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    // 重置到第一页
    setPagination({
      ...pagination,
      current: 1,
    });
  };

  const getFilteredCollections = () => {
    if (!searchText.trim()) {
      return collections;
    }
    
    const lowerSearchText = searchText.toLowerCase();
    return collections.filter(collection => {
      const nameMatch = collection.name?.toLowerCase().includes(lowerSearchText);
      return nameMatch;
    });
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '10%',
    },
    {
      title: '商品集名称',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      ellipsis: true,
    },
    {
      title: '商品数量',
      dataIndex: 'product_count',
      key: 'product_count',
      width: '15%',
      render: (count) => <Tag color="blue">{count} 个商品</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
      width: '20%',
    },
    {
      title: '操作',
      key: 'action',
      width: '25%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑名称
          </Button>
          <Button
            type="default"
            size="small"
            onClick={() => handleManageProducts(record)}
            style={{
              borderColor: '#52c41a',
              color: '#52c41a'
            }}
          >
            管理商品
          </Button>
          <Popconfirm
            title="确定要删除这个商品集吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredCollections = getFilteredCollections();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>商品集管理</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          添加商品集
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="搜索商品集名称..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={handleSearchChange}
          allowClear
          style={{ width: 400 }}
        />
        {searchText && (
          <span style={{ marginLeft: 12, color: '#888' }}>
            找到 {filteredCollections.length} 条结果
          </span>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={filteredCollections}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
          total: filteredCollections.length,
        }}
        onChange={handleTableChange}
        size="middle"
      />

      {/* 添加/编辑商品集对话框 */}
      <Modal
        title={editingCollection ? '编辑商品集' : '添加商品集'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText="确认"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="商品集名称"
            name="name"
            rules={[
              { required: true, message: '请输入商品集名称' },
              { max: 100, message: '商品集名称不能超过100个字符' },
            ]}
          >
            <Input placeholder="请输入商品集名称" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 管理商品集中的商品对话框 */}
      <Modal
        title={`管理商品集 - ${editingCollectionProducts?.name}`}
        open={productsModalVisible}
        onCancel={() => {
          setProductsModalVisible(false);
          setEditingCollectionProducts(null);
          setSelectedProducts([]);
          setProductsDetails([]);
          setSelectedProductsToDelete([]);
        }}
        onOk={handleProductsSubmit}
        okText="保存"
        cancelText="取消"
        width={800}
      >
        {loadingProducts ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <p style={{ marginTop: '16px', color: '#666' }}>加载商品列表...</p>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox
                  checked={selectedProductsToDelete.length === selectedProducts.length && selectedProducts.length > 0}
                  indeterminate={selectedProductsToDelete.length > 0 && selectedProductsToDelete.length < selectedProducts.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedProductsToDelete([...selectedProducts]);
                    } else {
                      setSelectedProductsToDelete([]);
                    }
                  }}
                />
                <p style={{ color: '#666', margin: 0 }}>
                  已有 {selectedProducts.length} 个商品
                </p>
              </div>
              <Space>
                <Button
                  type="primary"
                  danger
                  onClick={handleDeleteSelectedProducts}
                  disabled={selectedProductsToDelete.length === 0}
                >
                  删除选中 ({selectedProductsToDelete.length})
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddProductModalVisible(true)}
                >
                  添加商品
                </Button>
              </Space>
            </div>
            
            {productsDetails.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px 0', 
                backgroundColor: '#fafafa', 
                borderRadius: '8px',
                border: '1px dashed #d9d9d9'
              }}>
                <p style={{ color: '#999', marginBottom: 12 }}>商品集中暂无商品</p>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddProductModalVisible(true)}
                >
                  添加商品
                </Button>
              </div>
            ) : (
              <List
                bordered
                dataSource={productsDetails}
                style={{ maxHeight: 400, overflowY: 'auto' }}
                renderItem={(product) => (
                  <List.Item
                    key={product.id}
                    style={{ padding: '12px 16px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Checkbox
                        checked={selectedProductsToDelete.includes(product.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProductsToDelete([...selectedProductsToDelete, product.id]);
                          } else {
                            setSelectedProductsToDelete(
                              selectedProductsToDelete.filter(id => id !== product.id)
                            );
                          }
                        }}
                        style={{ marginRight: 12 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          {product.name}
                          {product.error && <Tag color="red" style={{ marginLeft: 8 }}>加载失败</Tag>}
                        </div>
                        <div style={{ color: '#888', fontSize: 12 }}>ID: {product.id}</div>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            )}

            {productsDetails.length > 0 && (
              <div style={{ 
                backgroundColor: '#f6ffed', 
                border: '1px solid #b7eb8f', 
                borderRadius: '6px', 
                padding: '12px', 
                marginTop: '16px' 
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  商品集包含 {selectedProducts.length} 个商品
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  提示: 选择商品后点击"删除选中"按钮，然后点击"保存"来更新商品集
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 添加商品选择器对话框 */}
      <Modal
        title="添加商品到商品集"
        open={addProductModalVisible}
        onCancel={() => setAddProductModalVisible(false)}
        footer={null}
        width={900}
        destroyOnClose={true}
      >
        {addProductModalVisible && (
          <ProductSearchSelector
            key="product-selector-modal"
            multiple={true}
            pageSize={10}
            onConfirm={handleAddProducts}
          />
        )}
      </Modal>

      <Divider />

      {/* 多级商品集分类编辑器 */}
      <MultiLevelJsonEditor
        data={multiLevelData}
        onDataChange={setMultiLevelData}
        onSave={saveMultiLevelData}
        loading={multiLevelLoading}
        title="多级商品集分类编辑"
        keyPrefix="col_"
        items={collections}
        itemIdField="id"
        itemNameField="name"
        leafNodeSuffix="(最终商品集)"
        addTopLevelText="添加顶级分类"
        addTopLevelLeafText="添加最终商品集作为顶级"
        selectModalTitle="选择要添加的商品集"
        selectLabel="商品集"
        selectPlaceholder="请选择商品集"
      />
    </div>
  );
};

export default CollectionManagement;

